import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Trans } from 'react-i18next'

interface RedirectNoticeProps {
  description?: string
  onRedirect: () => void
  delayMs?: number
}

const DEFAULT_DELAY_MS = 10000

export const RedirectNotice: React.FC<RedirectNoticeProps> = ({
  description,
  onRedirect,
  delayMs = DEFAULT_DELAY_MS
}) => {
  const initialSeconds = useMemo(() => Math.max(1, Math.round(delayMs / 1000)), [delayMs])
  const [remainingSeconds, setRemainingSeconds] = useState(initialSeconds)

  useEffect(() => {
    setRemainingSeconds(initialSeconds)

    if (delayMs <= 0) {
      onRedirect()
      return
    }

    const intervalId = window.setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev <= 1) {
          window.clearInterval(intervalId)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    const timeoutId = window.setTimeout(() => {
      onRedirect()
    }, delayMs)

    return () => {
      window.clearInterval(intervalId)
      window.clearTimeout(timeoutId)
    }
  }, [delayMs, initialSeconds, onRedirect])

  return (
    <Container>
      {description && <Description>{description}</Description>}
      <Notice>
        <Trans
          i18nKey="redirectNotice.message"
          count={remainingSeconds}
          values={{ count: remainingSeconds }}
          components={{
            Link: <RedirectLink type="button" onClick={onRedirect} />
          }}
        />
      </Notice>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  text-align: center;
  padding: ${({ theme }) => theme.spacing.md}rem;
`

const Description = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  line-height: 1.4;
`

const Notice = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  line-height: 1.5;
`

const RedirectLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  color: ${({ theme }) => theme.colors.primary};
  font: inherit;
  cursor: pointer;
  text-decoration: underline;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`

export default RedirectNotice
